import { Injectable } from '@angular/core';

import { ENV, FARO_API_KEY, FARO_RECEIVER_URL, IS_FARO_ENABLED, REGION, VERSION } from '@env';
import { FsTelemetryService, REGISTRY_ATTR, REGISTRY_EVENT } from '@axon-rto/fusus-web-telemetry';

/**
 * Registry-app facade over FsTelemetryService (FWX-5881).
 *
 * Centralizes the camera-registration funnel events so components stay clean
 * and event names/attributes live in one place. Every method is a thin wrapper
 * over `recordEvent` (a 0-duration Faro/PCG span). All calls are safe before
 * `init()` resolves and when telemetry is unconfigured — the underlying
 * service's no-op contract means nothing throws and nothing emits.
 */
@Injectable({ providedIn: 'root' })
export class RegistryTelemetryService {
  constructor(private readonly _telemetry: FsTelemetryService) {}

  /**
   * Boot the Faro transport once at app startup. Fire-and-forget: `init()`
   * lazy-loads the Faro SDK and opens the transport, so we never block
   * bootstrap on it. No-op when IS_FARO_ENABLED is false or the receiver
   * URL/key are empty.
   */
  public init(): void {
    void this._telemetry.init({
      serviceName: 'fusus-registry',
      serviceVersion: VERSION,
      serviceEnvironment: ENV,
      faroUrl: IS_FARO_ENABLED ? FARO_RECEIVER_URL : '',
      apiKey: IS_FARO_ENABLED ? FARO_API_KEY : '',
    });
  }

  public formViewed(): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.FORM_VIEWED, {
      [REGISTRY_ATTR.REGION]: REGION,
    });
  }

  public submitAttempted(): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.SUBMIT_ATTEMPTED, {
      [REGISTRY_ATTR.REGION]: REGION,
    });
  }

  public submitSucceeded(): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.SUBMIT_SUCCEEDED, {
      [REGISTRY_ATTR.REGION]: REGION,
    });
  }

  public submitFailed(status?: number): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.SUBMIT_FAILED, {
      [REGISTRY_ATTR.REGION]: REGION,
      [REGISTRY_ATTR.STATUS]: status ?? 0,
    });
  }

  public emailValidationViewed(): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.EMAIL_VALIDATION_VIEWED);
  }

  public emailValidationSucceeded(): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.EMAIL_VALIDATION_SUCCEEDED);
  }

  public emailValidationFailed(status?: number): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.EMAIL_VALIDATION_FAILED, {
      [REGISTRY_ATTR.STATUS]: status ?? 0,
    });
  }

  public statsViewed(): void {
    this._telemetry.recordEvent(REGISTRY_EVENT.STATS_VIEWED);
  }
}
